"use client";

import { useEffect, useState, useRef } from "react";
import { assetPath } from "../lib/utils";
import { AppIcon } from "./layout/AppIcon";
import { BarSpinner, barSpinnerKeyframes } from "./BarSpinner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SplashScreenProps {
  /** Called when the splash animation is complete. */
  onDone: () => void;
  /** Set to true when initial data is loaded. Splash waits for this. */
  dataReady?: boolean;
  /** Path to the logo image. Default: "/logo.png" */
  logoSrc?: string;
  /**
   * App-Kennung (z. B. "janeway-mail"). Ist sie gesetzt, zeichnet der Splash das
   * theme-faehige `AppIcon` statt der PNG-Grafik — dieselbe Kachel wie im
   * App-Switcher, also in der Farbe des gewaehlten Themes.
   *
   * Ohne sie bleibt es beim PNG: `logo.png` ist KEINE einfarbige Silhouette
   * (dunkle Kachel plus helles Glyph, 237 Farbwerte), eine CSS-Maske wuerde es
   * zu einer Flaeche verschmelzen und das Motiv zerstoeren.
   */
  appId?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SplashScreen({
  onDone,
  dataReady,
  logoSrc = "/logo.png",
  appId,
}: SplashScreenProps) {
  const [phase, setPhase] = useState<"in" | "hold" | "out">("in");
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Phase 1: "in" → "hold" after animation
  useEffect(() => {
    const t = setTimeout(() => setPhase("hold"), 100);
    return () => clearTimeout(t);
  }, []);

  // Phase 2: When data ready and in "hold" → start "out"
  useEffect(() => {
    if (phase === "hold" && dataReady) {
      setPhase("out");
    }
  }, [phase, dataReady]);

  // Phase 3: "out" → call onDone after fade
  useEffect(() => {
    if (phase !== "out") return;
    const t = setTimeout(() => onDoneRef.current(), 200);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <>
      <style>{barSpinnerKeyframes("blinkblur-bar")}</style>
      <div
        /* text-primary: die Ladebalken nutzen `backgroundColor: currentColor`
             und folgen so dem Theme. Sonst zeichnet der Splash keinen Text. */
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-background text-primary"
        style={{
          opacity: phase === "out" ? 0 : 1,
          pointerEvents: phase === "out" ? "none" : "auto",
          transition: phase === "out" ? "opacity 0.2s ease-in-out" : "none",
        }}
      >
        <div className="flex flex-col items-center" style={{ gap: 24 }}>
          {/* Logo */}
          <div
            style={{
              transform: phase === "in" ? "scale(0.82)" : "scale(1)",
              opacity: phase === "in" ? 0 : 1,
              transition:
                "transform 0.6s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease-out",
            }}
          >
            {appId ? (
              <AppIcon appId={appId} size={112} />
            ) : (
              <img
                src={assetPath(logoSrc)}
                alt="Janeway"
                style={{ width: 112, height: 112, objectFit: "contain" }}
              />
            )}
          </div>

          {/* Loading bars */}
          <BarSpinner
            keyframeName="blinkblur-bar"
            containerStyle={{
              opacity: phase === "hold" ? 1 : 0,
              transition: "opacity 0.35s ease-out",
            }}
          />
        </div>
      </div>
    </>
  );
}
